import { fetcher } from '../../../hooks/useSWR';

/** Document module name for CAPA Effectiveness reference document. */
export const CAPA_EFFECTIVENESS_REFERENCE_MODULE_NAME = 'CAPA_EFFECTIVENESS';

/**
 * Revision version from the document's current revision or Revisions list.
 * @param {object|null} doc
 * @param {object[]|null} [revisions]
 * @returns {string|null}
 */
export function getDocumentRevisionVersion(doc, revisions = null) {
  if (!doc) return null;

  const fromCurrent =
    doc.currentRevision?.version ??
    (typeof doc.currentRevision === 'string'
      ? doc.currentRevision.trim()
      : null);
  if (fromCurrent) return fromCurrent;

  const revisionList = revisions ?? doc.revisions;
  if (!Array.isArray(revisionList) || revisionList.length === 0) return null;

  if (doc.currentRevisionId != null) {
    const current = revisionList.find((r) => r.id === doc.currentRevisionId);
    if (current?.version) return current.version;
  }

  return revisionList[0]?.version ?? null;
}

async function loadRevisionsForDocument(documentId) {
  const revisions = await fetcher(
    `/document-management/revision/by-document/${documentId}`,
  );
  return Array.isArray(revisions) ? revisions : [];
}

async function enrichDocumentWithRevisionVersion(doc) {
  if (!doc?.id || getDocumentRevisionVersion(doc)) return doc;

  try {
    const revisions = await loadRevisionsForDocument(doc.id);
    const version = getDocumentRevisionVersion(doc, revisions);
    if (!version) return doc;

    return {
      ...doc,
      currentRevision: {
        ...(doc.currentRevision && typeof doc.currentRevision === 'object'
          ? doc.currentRevision
          : {}),
        version,
      },
    };
  } catch {
    return doc;
  }
}

/**
 * First document linked to the CAPA_EFFECTIVENESS document module.
 * @returns {Promise<object|null>}
 */
export async function fetchCapaEffectivenessReferenceDocument() {
  try {
    const documents = await fetcher(
      `/document-management/document?module=${encodeURIComponent(
        CAPA_EFFECTIVENESS_REFERENCE_MODULE_NAME,
      )}`,
    );
    const docsArray = Array.isArray(documents) ? documents : [];
    const doc = docsArray[0] ?? null;
    if (!doc) return null;
    return enrichDocumentWithRevisionVersion(doc);
  } catch {
    return null;
  }
}

/**
 * Display: document code - revision version.
 * @param {object|null} doc
 * @returns {string|null}
 */
export function formatCapaEffectivenessReferenceDocumentText(doc) {
  const documentCode = doc?.code?.trim();
  if (!documentCode) return null;

  const revisionVersion = getDocumentRevisionVersion(doc)?.trim();
  if (!revisionVersion) return documentCode;

  return `${documentCode} - ${revisionVersion}`;
}
